import React from "react";
import MessageInput from "pages/tickets/component/MessageInput";
import ActionsPanelComponent from "components/dxf-grid/actions-panel.component";
import { TicketMessage, TicketStatus, useTicketDetail, useTicketMessages } from "pages/tickets/ticket.service";
import Message from "pages/tickets/component/Message";
import { client } from "helpers/client";
import { toast } from "react-toastify";
import RefreshButton from "assets/images/svg/renew_green.svg";
import { useQueryClient } from "react-query";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";

import moment from "moment";
import { Spinner } from "react-bootstrap";
import { useStatus } from "hooks/use-status";
import { jsPDF } from "jspdf";
import { render } from "react-dom";
import html2canvas from "html2canvas";
import { useTranslation } from "react-i18next";
import CanLoadGuard from "components/private-route/can-load.guard";

interface TicketChatComponentProps {
  ticketId: string;
}

function TicketChatComponent({ ticketId }: TicketChatComponentProps) {
  const { t } = useTranslation("tickets");
  const { ticket, isLoading,refetch:refetchTicket } = useTicketDetail(ticketId);
  const { load, isLoading: actionLoading, done, stop } = useStatus();
  const chatDivRef = React.useRef<VirtuosoHandle>(null);
  const {
    messages: messagePages,
    fetchNextPage,
    isFetchingNextPage,
    totalCount,
    isSuccess,
    refetch,
    isFetching: isLoadingMessages
  } = useTicketMessages(ticketId);
  const queryClient = useQueryClient();

  const [allMessages, setAllMessages] = React.useState<TicketMessage[]>([]);

  React.useEffect(() => {
    if (!messagePages.length) return;
    setAllMessages(() => {
      const newMessages: TicketMessage[] = [];
      messagePages.forEach((p) => p?.messages.forEach((m) => newMessages.unshift(m)));
      return newMessages;
    });
  }, [messagePages]);

  async function cancelTicket() {
    load();
    const { error } = await client("/ticket/cancelTicket", { data: { ticketId } });
    if (error) return stop();
    refetchTicket();
    await queryClient.invalidateQueries("tickets");
    toast.success(t('common:form.canceled'));
    done();
  }

  async function closeTicket() {
    load();
    const { error } = await client("/ticket/closeTicket", { data: { ticketId, reason: "" } });
    if (error) return stop();
    refetchTicket();
    await queryClient.invalidateQueries("tickets");
    toast.success(t('common:form.closed'));
    done();
  }

  async function reOpenTicket() {
    load();
    const { error } = await client("/ticket/reopenTicket", { data: { ticketId, reason: "" } });
    if (error) return stop();
    refetchTicket();
    await queryClient.invalidateQueries("tickets");
    toast.success(t('common:form.opened'));
    done();
  }

  async function downloadTicket() {
    load();
    const { response } = await client<string>("/ticket/getTicketMessages", {
      params: {
        ticketId,
        pageNumber: 1,
        pageSize: totalCount
      }
    });
    if (response) {
      const data = JSON.parse(response) as { messages: TicketMessage[] };
      render(
        <div className="chat-pdf m-3 mt-5">
          {data.messages.map((m) => (
            <Message key={m.id} message={m} />
          ))}
        </div>,
        document.getElementById("download-messages-pdf")
      );
      const canvas = await html2canvas(document.getElementById("download-messages-pdf")!, {});
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      const doc = new jsPDF("p", "mm");
      let position = 0;
      console.log(heightLeft);

      doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        doc.addPage();
        doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      doc.save("file.pdf");
    }
    done();
  }

  function loadMoreMessages() {
    fetchNextPage();
  }

  const actions = React.useMemo(() => {
    if (!ticket) return [];
    switch (ticket.statusId) {
      case TicketStatus.Cancelled:
        return [{ label: t("download"), action: downloadTicket }];
      case TicketStatus.Closed:
        return [
          { label: t("reopen"), action: reOpenTicket },
          { label: t("download"), action: downloadTicket }
        ];
      case TicketStatus.Active:
        return [
          { label: t("cancel"), action: cancelTicket },
          {
            label: t("close"),
            action: closeTicket
          },
          { label: t("download"), action: downloadTicket }
        ];
    }
  }, [ticket?.statusId]);

  if (isLoading) return <Spinner animation="border" />;
  if (!ticket) return null;

  return (
    <div className="chat">
      <div className="title">
        <div className="info d-flex">
          <h2>{ticket.title}</h2>
          <p>{t("updateOn")} {moment(ticket.updateDate).format("hh:mm")}</p>
        </div>
        <div className="d-flex">
          <button className="btn-icon" onClick={() => refetch()}>
            <img src={RefreshButton} alt="refresh button" />
          </button>
          <CanLoadGuard>
            {actionLoading ? <Spinner animation="border" size="sm" /> : <ActionsPanelComponent actions={actions!} />}
          </CanLoadGuard>
        </div>
      </div>
      <hr className="hr mb-2" />
      <div id="download-messages-pdf" className="put-outside"></div>
      {/*<div className="date text-center">*/}
      {/*  <span>22/10/2020 - 08:03</span>*/}
      {/*</div>*/}
      {isFetchingNextPage && <p>loading...</p>}
      {isLoadingMessages && !isFetchingNextPage && <p>refreshing...</p>}
      {/*<button disabled={isFetchingNextPage} onClick={loadMoreMessages}>*/}
      {/*  {isFetchingNextPage ? "loading" : "load more"}*/}
      {/*</button>*/}

      {isSuccess && (
        <Virtuoso
          ref={chatDivRef}
          initialTopMostItemIndex={totalCount}
          className="chat-message"
          data={allMessages}
          atTopStateChange={(isAtTop) => isAtTop && loadMoreMessages()}
          style={{ height: "400px", overflowX: "hidden" }}
          followOutput="auto"
          itemContent={(i, message) => (
            <div key={message.id} className="mx-4">
              <Message key={message.id} message={message} />
            </div>
          )}
        />
      )}
      <hr className="hr" />
      <CanLoadGuard>
        <MessageInput
          ticket={ticket}
          onAddMessage={(message) => {
            setAllMessages((m) => [...m, message]);
            chatDivRef.current?.scrollToIndex({ index: "LAST" });
          }}
        />
      </CanLoadGuard>
    </div>
  );
}

export default TicketChatComponent;
